/**
 * The three-stage wash (spec 2026-08-23 §6.2). Pure state machine -- no DOM, no network -- so
 * the ordering rules are unit-testable and the screen keeps only the gesture wiring.
 *
 * Order is enforced here rather than server-side: the server clears stains in one call at the
 * end (§6.3), and the effect is purely cosmetic, so out-of-order protection is a UX affordance
 * rather than a security boundary.
 */
export const WASH_STAGES = [
  { code: 'rinse', itemCode: 'wash_pail', label: 'Rinse' },
  { code: 'lather', itemCode: 'wash_soap', label: 'Lather' },
  { code: 'dry', itemCode: 'wash_towel', label: 'Dry' },
] as const;

export type WashState = { stageIndex: number; done: boolean };

export function initialWash(): WashState {
  return { stageIndex: 0, done: false };
}

/** Whether an item code is one of the fixed wash tools. HabitatScreen's shake-completion
 *  handler (Task 16) uses this to route a completed shake to the wash machine instead of a
 *  care-action POST — D5: a wash tool must never touch a care stat. */
export function isWashTool(itemCode: string): boolean {
  return WASH_STAGES.some((s) => s.itemCode === itemCode);
}

/** Advances only for the DUE stage's item. Anything else is a no-op, including a later
 *  stage's tool -- picking the towel first highlights what is due, it does not error. */
export function advanceWash(state: WashState, itemCode: string): WashState {
  if (state.done) return state;
  if (WASH_STAGES[state.stageIndex]?.itemCode !== itemCode) return state;
  const stageIndex = state.stageIndex + 1;
  return { stageIndex, done: stageIndex >= WASH_STAGES.length };
}

/** Opacity multiplier for the stain overlay as the wash proceeds. Visual only -- the record
 *  changes once, at the end. */
export function stainFadeFor(state: WashState): number {
  const step = 1 / WASH_STAGES.length;
  return Math.max(0, 1 - state.stageIndex * step);
}
